import imageCompression from 'browser-image-compression';
import clsx, { ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import z, { ZodSchema, ZodType } from 'zod';

import { TFileValue } from '@components/ui/input/input-file/input-file-v1';

import { TDate, TObject, TResponseAPI, TTypeActionData } from '@typescript/index-type';
import { TOption, TTypeDateFormat, TTypeFile } from '@typescript/ui-types';

export const cn = (...inputs: ClassValue[]) => {
    return twMerge(clsx(inputs));
};

export const isEmptyValue = (value: any): boolean => {
    if ([undefined, null, '']?.includes(value)) return true;
    if (typeof value === 'object') {
        if (Array.isArray(value)) return value.length === 0;
        return Object.keys(value).length === 0;
    }
    return false;
};

interface TParamsSpreadArrayTemp {
    newValue: any;
    array: any[];
}

export const spreadArrayAttemp = (params: TParamsSpreadArrayTemp) => {
    const { newValue, array } = params;
    return isEmptyValue(array) ? [newValue] : [...array, newValue];
};

export const arraysHaveSameMembers = (array1: string[], array2: string[]) => {
    if (array1.length !== array2.length) return false;

    const sortedArray1 = array1.slice().sort();
    const sortedArray2 = array2.slice().sort();

    return sortedArray1.every((value, index) => value === sortedArray2[index]);
};

export const handlePreventDefault = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    return e?.preventDefault();
};

interface TParamsFieldFromObjectList {
    array: any[];
    fieldNameTarget: string;
    fieldNameValue: string;
    value: any;
}
export const getFieldFromObjectList = (params: TParamsFieldFromObjectList) => {
    const { array, fieldNameTarget, fieldNameValue, value } = params;
    return array?.filter((data) => data?.[fieldNameValue] === value)?.[0]?.[fieldNameTarget];
};

export const getFieldLabelFromOptions = (params: Pick<TParamsFieldFromObjectList, 'array' | 'value'>) => {
    const { array, value } = params;
    return array?.filter((data) => data?.value === value)?.[0]?.label;
};

export type DebouncedFunction<T extends (...args: any[]) => void> = T & {
    cancel: () => void;
};

export function debounce<T extends (...args: any[]) => void>(func?: T, wait: number = 0): DebouncedFunction<(...args: Parameters<T>) => void> {
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const debounced = (...args: Parameters<T>) => {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
            func?.(...args);
        }, wait);
    };

    debounced.cancel = () => {
        if (timeout) {
            clearTimeout(timeout);
            timeout = undefined;
        }
    };

    return debounced as DebouncedFunction<(...args: Parameters<T>) => void>;
}

interface TParamsGetAssetURl {
    name: string;
    folder?: 'images' | 'icons';
}
export const getAssetURL = (params: TParamsGetAssetURl) => {
    const { name, folder = 'images' } = params;
    return new URL(`../../assets/${folder}/${name}`, import.meta.url)?.href;
};

export const convertBytesToMegabytes = (bytes: number): number => {
    return bytes / (1024 * 1024);
};

export const handleGetFileTypeFromName = (name: string) => {
    const type = name?.split('.')?.slice(-1)[0];

    return `.${type?.toLowerCase()}`;
};

export const isValidTypeFile = (params: { file: File; listAcceptedTypeFile: TTypeFile[] }) => {
    const { file, listAcceptedTypeFile } = params;
    const type = handleGetFileTypeFromName(file?.name) as TTypeFile;

    const isAllTypeAllow = listAcceptedTypeFile?.includes(TTypeFile.ALL);
    const isAllImageTypeAllow = listAcceptedTypeFile?.includes(TTypeFile.IMAGE_ALL);
    const isTypeAllow = isAllTypeAllow || listAcceptedTypeFile?.includes(type) || isAllImageTypeAllow;

    return isTypeAllow;
};

interface TParamsDownloadFile {
    url: string;
    filename: string;
}
export const handleDownloadFile = (params: TParamsDownloadFile) => {
    const { url, filename } = params;

    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename || 'file';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
};

export type TTypeGeneralFile = 'image' | 'pdf' | undefined;

export const getGeneralTypeFile = (type: string): TTypeGeneralFile => {
    let generalType;
    if (['jpg', 'jpeg', 'png', 'webp']?.some((ext) => type?.toLowerCase()?.includes(ext))) {
        generalType = 'image';
    } else if (type?.includes('pdf')) {
        generalType = 'pdf';
    }

    return generalType as TTypeGeneralFile;
};

export const extractExtensionFile = (type: string) => {
    const [_, extension] = type.split('/');
    return extension;
};

export const excludeRef = <T extends { ref?: any }>(input: T) => {
    const { ref: _, ...rest } = input;
    return rest;
};

export const delay = (ms: number): Promise<void> => {
    return new Promise((resolve) => setTimeout(resolve, ms));
};

export const generateUrlQueryParams = (params: { url: string; queryObject: TObject }) => {
    const { url, queryObject } = params;
    const queryParams = new URLSearchParams(queryObject);
    const newUrl = `${url}?${queryParams.toString()}`;
    return newUrl;
};

export const generateDefaultValue = (schema: ZodType<any>): any => {
    if (schema instanceof z.ZodString) return '';
    if (schema instanceof z.ZodObject) {
        const shape = schema.shape;
        const defaultObj: { [key: string]: any } = {};
        for (const key in shape) {
            defaultObj[key] = generateDefaultValue(shape[key]);
        }
        return defaultObj;
    }
    if (schema instanceof z.ZodArray) return null;
    if (schema instanceof z.ZodOptional) return null;
    if (schema instanceof z.ZodNull) return null;
    if (schema instanceof z.ZodEffects) return '';
};

export const generateOptions = (params: { options: TObject; labelField?: string; valueField?: string; listSaveField?: string[]; isFormatCapitalize?: boolean }) => {
    const { options, isFormatCapitalize = true, labelField = 'name', valueField = 'id', listSaveField } = params;

    return options?.map((option: TObject) => {
        const saveFields = listSaveField?.reduce<Record<string, any>>((acc, field) => {
            acc[field] = option[field];
            return acc;
        }, {});
        return {
            label: isFormatCapitalize ? toCapitalize(option[labelField]) : option[labelField],
            value: option?.[valueField]?.toString(),
            ...saveFields,
        };
    });
};

export const generateOptionsFromEnum = (enumObject: TObject): TOption<string>[] => {
    return Object?.keys(enumObject)?.map((key) => {
        return {
            label: toCapitalize(enumObject[key]?.replace(/_/g, ' ')),
            value: enumObject[key],
        };
    });
};

export const generateFileFromUrl = async (url: string) => {
    const blob = await fetch(url).then((r) => r.blob());
    const file = new File([blob], 'random', { type: blob.type });
    return file;
};

type FormField = {
    value: any;
    name: string;
    isUpdated?: boolean;
};

export const extractValueFromForm = <TForm extends Record<string, FormField>>(form: TForm, action?: TTypeActionData): Partial<{ [K in keyof TForm]: TForm[K]['value'] }> => {
    return Object.keys(form).reduce(
        (acc, key) => {
            const field = form[key as keyof TForm];

            if (action === TTypeActionData['EDIT'] && !field?.isUpdated && field.name !== 'id') {
                return acc;
            }

            acc[key as keyof TForm] = field?.value;
            return acc;
        },
        {} as Partial<{ [K in keyof TForm]: TForm[K]['value'] }>,
    );
};

interface TMappingErrorsToForm<TSchema, TForm extends TObject> {
    schema: ZodSchema<TSchema>;
    form: TForm;
}

export const mappingErrorsToForm = <TSchema, TForm extends TObject>(params: TMappingErrorsToForm<TSchema, TForm>) => {
    type TKForm = keyof TForm;

    const { schema, form } = params;
    let isValid = true;

    const result = schema.safeParse(
        Object.keys(form).reduce((acc, key) => {
            const value = form[key as TKForm].value;
            acc[key] = typeof value === 'string' ? value?.trim() : value;
            return acc;
        }, {} as TObject),
    );
    isValid = result?.success;
    Object?.keys(form)?.reduce((acc, key) => {
        const errorMessage = !isValid ? result?.error?.flatten()?.fieldErrors[key as TKForm]?.[0] : '';
        form[key as TKForm].errorMessage = errorMessage;
        return acc;
    }, {} as TObject);

    return { isValid, form };
};

interface TMappingValuesToForm<TForm extends TObject> {
    values: Partial<Record<keyof TForm, any>>;
    form: TForm;
}
export const mappingValuesToForm = <TForm extends TObject>(params: TMappingValuesToForm<TForm>) => {
    const { form, values } = params;

    Object.keys(form).forEach((key) => {
        form[key].value = values[key] || '';
    });

    return { ...form };
};

export const fetchOptions = async <T>(fetchFunction: (params?: any) => Promise<T[]>, params?: any) => {
    const data = await fetchFunction(params);
    return generateOptions({ options: data });
};

export const isHtmlHasText = (html: string): boolean => {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = html;

    return (tempElement?.textContent?.trim().length || 0) > 0;
};

export const deepCopy = <T extends object>(input: T): T => {
    if (input === null || typeof input !== 'object') return input;
    if (input instanceof Date) return new Date(input.getTime()) as T;
    if (Array.isArray(input)) return input.map((item) => deepCopy(item)) as unknown as T;

    const result: any = {};
    for (const key in input) {
        if (Object.prototype.hasOwnProperty.call(input, key)) {
            const value = (input as any)[key];
            result[key] = deepCopy(value);
        }
    }

    return result;
};

interface TFormatDate {
    date: TDate;
    format?: TTypeDateFormat;
    timeZone?: string;
    formatLanguage?: 'en-GB' | 'en-US';
}
export const formatDate = (params: TFormatDate): string => {
    let { date } = params;
    const { format = TTypeDateFormat['DD/MM/YYYY'], timeZone = 'UTC', formatLanguage = 'en-GB' } = params;

    if (!date) return '';

    date = new Date(date);

    if (format === TTypeDateFormat.ISO) return date?.toISOString();

    const formatOptions: { [key in TTypeDateFormat]?: Intl.DateTimeFormatOptions } = {
        [TTypeDateFormat['DD MONTH YEAR']]: {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            timeZone,
        },
        [TTypeDateFormat['DD-MM-YYYY']]: {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            timeZone,
        },
        [TTypeDateFormat['DD/MM/YYYY']]: {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            timeZone,
        },
        [TTypeDateFormat['hh:mm']]: { hour: '2-digit', minute: '2-digit', hour12: false, timeZone },
    };

    const formattedDate = new Intl.DateTimeFormat(formatLanguage, formatOptions[format]!).format(date);
    return (format === TTypeDateFormat['DD-MM-YYYY'] ? formattedDate.replace(/\//g, '-') : formattedDate) || '';
};

export const formatDateForPresent = (date: Date) => {
    return formatDate({ date: toLocalDateInputValue(date), format: TTypeDateFormat['DD MONTH YEAR'] });
};

export const generateMaxDateOneYear = (date: string | Date | null): Date | null => {
    if (!date) return null;
    const parseDate = new Date(date);
    const maxDate = parseDate.setFullYear(parseDate.getFullYear() + 1);
    return new Date(maxDate);
};

export const getRandomKey = <T extends object>(obj: T): keyof T => {
    const keys = Object.keys(obj) as Array<keyof T>;

    const randomIndex = Math.floor(Math.random() * keys.length);
    return keys[randomIndex];
};

export const toCapitalize = (str: string) => {
    return str
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1)?.toLowerCase())
        .join(' ');
};

export const catchErrors = <T, Args extends any[]>(fn: (...args: Args) => T | Promise<T>) => {
    return async (...args: Args): Promise<T | void> => {
        try {
            return await fn(...args);
        } catch (error: any) {
            console.log('error message: ', error?.message);
        }
    };
};

interface TParamsFilterKeysObject<TObject extends object> {
    object: TObject;
    keys: (keyof TObject)[];
}
export const filterKeysObject = <TObject extends object>(params: TParamsFilterKeysObject<TObject>) => {
    const { object, keys } = params;

    keys?.forEach((key) => {
        delete object[key];
    });

    return object;
};

export type TParamsMapErrorMessagePromiseAll = (TResponseAPI<TObject> & { moduleName: string })[];
export const mapErrorMessagePromiseAll = (params: TParamsMapErrorMessagePromiseAll): string => {
    const results = params;

    const listErrorMaessage = results?.map((result) => {
        return `${result?.status ? 'Success' : 'Failed'} update ${result?.moduleName}`;
    });

    const errorMessage = listErrorMaessage?.reduce((acc, message, i) => {
        if (i === 0) return message;

        const seperator = i === listErrorMaessage?.length - 1 ? ' and ' : ', ';
        return `${acc}${seperator}${message}`;
    }, '');

    return errorMessage;
};

export const mergeArraysOfObjects = (arr1: object[], arr2: object[]): object[] => {
    return arr1.map((obj, index) => ({
        ...obj,
        ...arr2[index],
    }));
};

export const removeKeyWithUndifienedValue = <TData extends object>(obj: TData) => {
    return Object.fromEntries(Object.entries(obj).filter(([_, value]) => value !== undefined));
};

export const formatPhoneNumber = (phoneNumber: string): string => {
    const isHaveFormated = phoneNumber?.charAt(0) === '0';
    const formatedPhoneNumber = isHaveFormated ? phoneNumber : `0${phoneNumber?.replace(/-/gi, '')}`;

    return formatedPhoneNumber;
};

export const buildFormData = (data: Record<string, any>) => {
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
        if (value === undefined || value === null) return;

        if (Array.isArray(value) && value[0] instanceof File) {
            value.forEach((file: File) => {
                formData.append(key, file);
            });
            return;
        }

        if (value instanceof File) {
            formData.append(key, value);
            return;
        }

        formData.append(key, String(value));
    });

    return formData;
};

export type ImageCompressionOptions = {
    maxSizeMB?: number;
    maxWidthOrHeight?: number;
    useWebWorker?: boolean;
    initialQuality?: number;
};

export const compressImage = async (file: File, options?: ImageCompressionOptions): Promise<File> => {
    if (!file.type.startsWith('image/')) return file;

    const mergedOptions = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        initialQuality: 0.8,
        ...options,
    };
    const compressed = await imageCompression(file, mergedOptions);

    if (compressed instanceof File) {
        return compressed;
    }

    return new File([compressed], file.name, {
        type: (compressed as TFileValue)?.type || file.type,
        lastModified: Date.now(),
    });
};

export function omitKeys<T extends Record<string, any>, K extends readonly (keyof T)[]>(obj: T, keys: K): Omit<T, K[number]> {
    const result = { ...obj };

    keys.forEach((key) => {
        delete result[key];
    });

    return result;
}

export const convertEnumToLabel = (str: string) => {
    return str
        .toLowerCase()
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export function toLocalDateInputValue(isoString: string | Date | null) {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-CA');
}
